import React, { useState, useEffect, useCallback, useMemo } from "react";
import Popup from "../Modal/Popup";
import { Input, Checkbox, Space, message, ColorPicker } from "antd";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { set_chatbot_url, set_chatbot_display, set_chatbot_button_color, set_chatbot_button_text_color, set_chatbot_button_text } from "../../store/Actions/actions";

const ChatbotSettingsModal = ({
    closeModal
}) => {
    const dispatch = useDispatch();
    const { chatbot } = useSelector((state) => state.present);
    const [urlError, setUrlError] = useState("");
    const [isValidUrl, setIsValidUrl] = useState(true);
    const [localUrl, setLocalUrl] = useState(chatbot.url || "");
    const [localButtonText, setLocalButtonText] = useState(chatbot.buttonText || "Chat with us");
    const modalContentRef = React.useRef(null);

    const validateUrl = (url) => {
        if (!url) {
            setUrlError("");
            setIsValidUrl(true);
            return true;
        }

        const errorMessage = "URL must be a valid Flimp Chatbot URL";
        try {
            const urlObj = new URL(url);
            const baseUrl = urlObj.hostname; // This gets the domain part

            if (!url.startsWith('https://')) {
                setUrlError(errorMessage);
                setIsValidUrl(false);
                return false;
            }

            if (!baseUrl.includes('flimp')) {
                setUrlError(errorMessage);
                setIsValidUrl(false);
                return false;
            }

            setUrlError("");
            setIsValidUrl(true);
            return true;
        } catch (error) {
            setUrlError(errorMessage);
            setIsValidUrl(false);
            return false;
        }
    };

    const handleUrlChange = (e) => {
        const newUrl = e.target.value;
        setLocalUrl(newUrl);
        validateUrl(newUrl);
    };

    const handleUrlBlur = (e) => {
        const url = e.target.value;
        if (validateUrl(url)) {
            // Only dispatch to Redux if valid
            dispatch(set_chatbot_url(url));
        }
    };

    const handleVisibilityChange = (e) => {
        const newVisibility = e.target.checked;
        // Store in Redux
        dispatch(set_chatbot_display(newVisibility));
    };

    const handleColorChange = useCallback((color) => {
        const hexColor = typeof color === 'string' ? color : color.toHexString();
        dispatch(set_chatbot_button_color(hexColor));

        // Calculate and save the text color
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = (299 * r + 587 * g + 114 * b) / 1000;
        const textColor = brightness > 128 ? "#000000" : "#ffffff";
        dispatch(set_chatbot_button_text_color(textColor));
    }, [dispatch]);

    const handleButtonTextChange = (e) => {
        const newText = e.target.value;
        setLocalButtonText(newText);
        dispatch(set_chatbot_button_text(newText));
    };

    const handleButtonTextBlur = () => {
        // If user cleared the text, revert to default "Chat with us"
        if (!localButtonText || localButtonText.trim() === "") {
            const defaultText = "Chat with us";
            setLocalButtonText(defaultText);
            dispatch(set_chatbot_button_text(defaultText));
        } else {
            dispatch(set_chatbot_button_text(localButtonText));
        }
    };

    // Calculate brightness to determine if text should be black or white
    const calculateBrightness = (hexColor) => {
        // Remove # if present
        const hex = hexColor.replace('#', '');
        // Convert to RGB
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        // Calculate relative luminance (perceived brightness)
        // Using the formula: (299*R + 587*G + 114*B) / 1000
        return (299 * r + 587 * g + 114 * b) / 1000;
    };

    const getTextColor = () => {
        const buttonColor = chatbot.buttonColor || "#1A202C";
        const brightness = calculateBrightness(buttonColor);
        // If brightness is greater than 128, use black text; otherwise use white
        return brightness > 128 ? "#000000" : "#ffffff";
    };

    const handleSave = () => {
        if (chatbot.url && !isValidUrl) {
            message.error("Please fix the URL validation errors before saving");
            return;
        }
        // If button text is empty when saving, revert to default
        if (!localButtonText || localButtonText.trim() === "") {
            const defaultText = "Chat with us";
            dispatch(set_chatbot_button_text(defaultText));
        } else {
            dispatch(set_chatbot_button_text(localButtonText));
        }
        closeModal();
    };

    const handleClose = () => {
        // If button text is empty when closing, revert to default
        if (!localButtonText || localButtonText.trim() === "") {
            dispatch(set_chatbot_button_text("Chat with us"));
        } else {
            dispatch(set_chatbot_button_text(localButtonText));
        }
        closeModal();
        // If URL becomes empty, automatically uncheck display and dispatch
        if (!localUrl) {
            dispatch(set_chatbot_display(false));
        }
    };


    // Allow ColorPicker popup to overflow outside modal
    useEffect(() => {
        // Check if style already exists to prevent duplicates
        let style = document.getElementById('chatbot-modal-overflow-fix');
        if (!style) {
            style = document.createElement('style');
            style.id = 'chatbot-modal-overflow-fix';
            style.innerHTML = `
                #saveOrCancelItWrapper .modalContent {
                    overflow: visible !important;
                }
                #saveOrCancelItWrapper .modalBody {
                    overflow: visible !important;
                }
            `;
            document.head.appendChild(style);
        }

        return () => {
            // Only remove if this is the last instance
            const existingStyle = document.getElementById('chatbot-modal-overflow-fix');
            if (existingStyle) {
                existingStyle.remove();
            }
        };
    }, []);

    return (
        <Popup
            width="700"
            header="Chatbot Settings"
            parentID="saveOrCancelItWrapper"
            close={handleClose}
            onOk={handleSave}
            okText="Save"
            cancelText="Cancel"
        >
            <div ref={modalContentRef} style={{ padding: "20px" }}>
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    <div>
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                            Add chatbot URL
                        </label>
                        <Input
                            placeholder="Enter chatbot URL (e.g., https://flimp.live/chatbot123)"
                            value={localUrl}
                            onChange={handleUrlChange}
                            onBlur={handleUrlBlur}
                            status={urlError ? "error" : ""}
                            style={{ width: "100%" }}
                        />
                        {urlError && (
                            <div style={{
                                color: "#ff4d4f",
                                fontSize: "12px",
                                marginTop: "4px"
                            }}>
                                {urlError}
                            </div>
                        )}
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                            Button text
                        </label>
                        <Input
                            placeholder="Enter button text"
                            value={localButtonText}
                            onChange={handleButtonTextChange}
                            onBlur={handleButtonTextBlur}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Checkbox
                            checked={!!chatbot.display}
                            onChange={handleVisibilityChange}
                        >
                            Show / Hide chatbot
                        </Checkbox>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ fontSize: "14px", fontWeight: "500" }}>Button Color:</span>
                            <ColorPicker
                                id="chatbot-color-picker"
                                value={chatbot.buttonColor || "#1A202C"}
                                onChangeComplete={handleColorChange}
                                showText
                                placement="bottomRight"
                                getPopupContainer={() => modalContentRef.current || document.body}
                            />
                        </div>
                    </div>
                </Space>
            </div>
        </Popup>
    );
};

export default ChatbotSettingsModal;

ChatbotSettingsModal.propTypes = {
    closeModal: PropTypes.func,
};
